import React, { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import AllSpecifications from 'containers/Specifications/AllSpecifications';
import Filters from 'containers/Specifications/Filters/Filters';
import FolderView from 'containers/Specifications/FolderView';
import FileIcon from 'images/v5/icons/file-04.svg';
import Breadcrumbs from 'layout/atoms/Breadcrumbs/Breadcrumbs';
import Label from 'layout/atoms/Label';
import Loader from 'layout/atoms/Loader/Loader';
import Modal from 'layout/atoms/Modal/Modal';
import Grid from 'layout/molecules/Grid/Grid';
import FoldersTree from 'layout/organisms/FoldersTree/FoldersTree';
import Submenu from 'layout/organisms/Submenu/Submenu';
import Dashboard from 'layout/templates/Dashboard/Dashboard';
import { fetchRootFolder, fetchRootFolders, selectFolderName, selectFoldersTree, selectRootFolder, } from 'store/specificationsFoldersSlice';
function SpecificationsPicker() {
    var _a;
    const { t } = useTranslation(['specification', 'common', 'orders']);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { warehouseReleaseId } = useParams();
    const rootFolder = useSelector(selectRootFolder);
    const url = `/warehouse-releases/${warehouseReleaseId}/summary/specifications`;
    const isMyFiles = location.pathname === `${url}/my-files`;
    const isFolderView = location.pathname.includes(`${url}/folders/`);
    const folderId = ((_a = location.pathname.match(/folders\/([^/]+)/)) === null || _a === void 0 ? void 0 : _a[1]) || (rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId);
    const folderName = useSelector((state) => selectFolderName(state, folderId));
    const foldersTree = useSelector(selectFoldersTree);
    const memoFoldersTree = useMemo(() => foldersTree, [foldersTree]);
    useEffect(() => {
        dispatch(fetchRootFolder());
    }, [dispatch]);
    useEffect(() => {
        if (rootFolder.rootFolderId !== null) {
            dispatch(fetchRootFolders({ folderId: rootFolder.rootFolderId }));
        }
    }, [dispatch, rootFolder.status, rootFolder.rootFolderId]);
    const generateBreadcrumbs = (folders, currentFolderId) => {
        const breadcrumbs = [];
        let currentFolder = folders.find((folder) => folder.id === currentFolderId);
        while (currentFolder) {
            breadcrumbs.unshift({
                id: currentFolder.id,
                to: `${url}/folders/${currentFolder.id}`,
                name: currentFolder.name,
            });
            // eslint-disable-next-line no-loop-func
            currentFolder = folders.find((folder) => folder.id === currentFolder.parentId);
        }
        return breadcrumbs;
    };
    const folderBreadcrumbs = memoFoldersTree ? generateBreadcrumbs(memoFoldersTree, folderId) : [];
    const handleCancel = () => navigate(`/warehouse-releases/${warehouseReleaseId}/summary`);
    const defaultLinks = [
        {
            id: 'my_files',
            to: `${url}/my-files`,
            text: t('specification:my_files'),
            icon: React.createElement(FileIcon, null),
        },
    ];
    const isLoading = rootFolder.status === 'idle' || rootFolder.status === 'loading';
    return (React.createElement(Modal, { name: "warehouseReleaceSpecificationPicker", size: "fullScreen", header: React.createElement(Label, { weight: "strong" }, t('common:select_specification')), noPadding: true, onClose: handleCancel },
        isLoading && React.createElement(Loader, null),
        React.createElement("div", { style: { display: isLoading ? 'none' : undefined } },
            React.createElement(Dashboard, { asideMenu: React.createElement(Grid, { vp: 12, hp: 12, gap: 0 },
                    React.createElement(Submenu, { groups: [{ id: 'defaultLinks', items: defaultLinks }] }),
                    React.createElement(FoldersTree, { rootFolderId: rootFolder.rootFolderId, folders: memoFoldersTree, link: `${url}/folders` })) },
                React.createElement(Grid, { vp: 12, hp: 24, gap: 0 }, 
                // eslint-disable-next-line no-nested-ternary
                isMyFiles ? (React.createElement(React.Fragment, null,
                    React.createElement(Breadcrumbs, { items: [
                            {
                                id: 'specifications/',
                                to: url,
                                name: t('specification_capitalize_other'),
                            },
                            { id: 'my_files', to: `${url}/my-files`, name: t('my_files') },
                        ] }),
                    React.createElement("h2", null, t('my_files')))) : isFolderView ? (React.createElement(React.Fragment, null,
                    React.createElement(Breadcrumbs, { items: [
                            {
                                id: '/specifications/',
                                to: url,
                                name: t('specification_capitalize_other'),
                            },
                            ...folderBreadcrumbs,
                        ] }),
                    React.createElement("h2", null, folderId === rootFolder.rootFolderId
                        ? t('folder_capitalize_other')
                        : folderName))) : null),
                // eslint-disable-next-line no-nested-ternary
                isMyFiles ? React.createElement(Filters, null) : isFolderView ? React.createElement(Filters, { folders: true }) : null,
                React.createElement(Grid, { vp: 12, hp: 24, gap: 0 }, 
                // eslint-disable-next-line no-nested-ternary
                isMyFiles ? (React.createElement(AllSpecifications, { warehouseReleaseSelect: true })) : isFolderView ? (React.createElement(FolderView, { link: `${url}/folders`, warehouseReleaseSelect: true, folderId: folderId })) : ((rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId) && (React.createElement(Navigate, { to: `${url}/folders/${rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId}`, replace: true }))))))));
}
export default memo(SpecificationsPicker);
